import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wallet, ArrowDownCircle, ArrowUpCircle, Clock, CheckCircle2, XCircle, TrendingUp, QrCode, FlaskConical, CreditCard } from "lucide-react";
import CardPaymentDialog from "@/components/CardPaymentDialog";
import SavedCardsCard from "@/components/SavedCardsCard";
import ReceiptDialog from "@/components/ReceiptDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import PixPaymentDialog from "@/components/PixPaymentDialog";
import PagarmePixDialog from "@/components/PagarmePixDialog";
import AutoDepositCard from "@/components/AutoDepositCard";
import { FxDepositDialog, CryptoDepositDialog } from "@/components/FxCryptoDeposit";
import WithdrawGateCard from "@/components/WithdrawGateCard";
import { DollarSign, Euro, Bitcoin } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyle = { pending: "bg-orange-500", approved: "bg-blue-500", paid: "bg-green-600", denied: "bg-red-500" };
const statusLabel = { pending: "Pendente", approved: "Aprovado", paid: "Pago", denied: "Negado" };

export default function WalletPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [investor, setInvestor] = useState(null);
  const [depositAmount, setDepositAmount] = useState(50);
  const [pixOpen, setPixOpen] = useState(false);
  const [pagarmePixOpen, setPagarmePixOpen] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [fxOpen, setFxOpen] = useState(null); // "USD" | "EUR"
  const [cryptoOpen, setCryptoOpen] = useState(false);
  const [cryptoTx, setCryptoTx] = useState(null);
  // Return from Stripe (fx_session) / CoinGate (crypto_tx)
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const fxs = q.get("fx_session"); const ctx = q.get("crypto_tx");
    if (fxs) {
      axios.get(`${API}/wallet/fx/status/${fxs}`).then((r) => {
        if (r.data.credited) toast.success(`Depósito em ${r.data.currency} confirmado: R$ ${Number(r.data.brl_amount).toFixed(2)} creditados!`);
        else toast.info("Pagamento ainda não confirmado. O saldo será creditado automaticamente.");
        load();
      }).catch(() => {});
      window.history.replaceState({}, "", "/wallet");
    } else if (q.get("fx_cancel")) { toast.info("Pagamento cancelado."); window.history.replaceState({}, "", "/wallet"); }
    if (ctx) { setCryptoTx(ctx); setCryptoOpen(true); window.history.replaceState({}, "", "/wallet"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [cardsKey, setCardsKey] = useState(0);
  const [pagarmeStatus, setPagarmeStatus] = useState(null);
  const [methods, setMethods] = useState({ pagarme_pix: true, manual_pix: true, card: true, manual_pix_configured: true });
  const [depositDialogOpen, setDepositDialogOpen] = useState(() => new URLSearchParams(window.location.search).get("topup") === "1");
  const [withdrawForm, setWithdrawForm] = useState({ amount: "", method: "pix", destination: user?.pix_key || "" });
  const [openW, setOpenW] = useState(false);
  const [openInvW, setOpenInvW] = useState(null); // investment being withdrawn
  const [invWForm, setInvWForm] = useState({ method: "pix", destination: "" });
  const [wdStep, setWdStep] = useState("form"); // form | code
  const [wdRequest, setWdRequest] = useState(null); // { request_id, email_sent_to, dev_code, mocked, expires_at }
  const [wdCode, setWdCode] = useState("");
  const [wdSubmitting, setWdSubmitting] = useState(false);

  const load = async () => {
    const [w, i] = await Promise.all([
      axios.get(`${API}/wallet/me`),
      axios.get(`${API}/investor/me`),
    ]);
    setData(w.data);
    setInvestor(i.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Detect Pagar.me availability once on mount — silent failure is fine.
    axios.get(`${API}/pagarme/status`).then(r => setPagarmeStatus(r.data)).catch(() => {});
    axios.get(`${API}/payments/methods`).then(r => setMethods(r.data)).catch(() => {});
  }, []);

  // Stripe deposit flow was retired in iteration 44 — now uses Pagar.me Pix (primary)
  // with the manual admin-confirmed Pix as a fallback when Pagar.me isn't configured.

  const resetWithdraw = () => {
    setWdStep("form");
    setWdRequest(null);
    setWdCode("");
    setWithdrawForm({ amount: "", method: "pix", destination: user?.pix_key || "" });
  };

  const requestWithdraw = async () => {
    const amt = parseFloat(withdrawForm.amount);
    if (!amt) return toast.error("Informe um valor");
    setWdSubmitting(true);
    try {
      const res = await axios.post(`${API}/wallet/withdraw`, { ...withdrawForm, amount: amt });
      setWdRequest(res.data);
      setWdStep("code");
      toast.success(res.data.mocked
        ? "Modo dev: código exibido abaixo"
        : `Código enviado para ${res.data.email_sent_to}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setWdSubmitting(false);
  };

  const confirmWithdraw = async () => {
    if (!wdCode || wdCode.trim().length < 4) return toast.error("Informe o código");
    setWdSubmitting(true);
    try {
      const res = await axios.post(`${API}/wallet/withdraw/confirm`, { request_id: wdRequest.request_id, code: wdCode.trim() });
      toast.success(res.data.status === "paid" ? "Saque processado instantaneamente!" : "Solicitação confirmada. Aguarde aprovação.");
      setOpenW(false);
      resetWithdraw();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    setWdSubmitting(false);
  };

  const withdrawInvestment = async () => {
    if (!invWForm.destination.trim()) return toast.error("Informe destino");
    try {
      await axios.post(`${API}/investor/withdraw`, { ...invWForm, investment_id: openInvW.investment_id });
      toast.success("Resgate solicitado! Aguarde processamento.");
      setOpenInvW(null);
      setInvWForm({ method: "pix", destination: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  if (!data || !investor) return <div className="text-center py-20">Carregando...</div>;

  const fee = data.withdrawal_fee_pct;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
          <Wallet className="w-6 h-6 text-primary-foreground" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Minha carteira</h1>
          <p className="text-sm text-muted-foreground">Depósitos, saques e investimentos</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Wallet card */}
        <Card className="p-6 bg-gradient-to-br from-primary/10 to-transparent border-primary/30" data-testid="wallet-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Saldo carteira</div>
            <Wallet className="w-4 h-4 text-primary" />
          </div>
          <div className="text-4xl font-bold tracking-tight" data-testid="wallet-balance">R$ {data.wallet_balance.toFixed(2)}</div>
          <div className="text-xs text-muted-foreground mt-1">
            Taxa de saque: {fee}% · Limite diário: R$ {data.daily_limit.toFixed(2)}
          </div>
          <div className="flex gap-2 mt-4">
            <Dialog open={depositDialogOpen} onOpenChange={setDepositDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="deposit-btn" size="sm" className="rounded-full gap-1"><ArrowDownCircle className="w-4 h-4" />Depositar</Button>
              </DialogTrigger>
              <DialogContent className="max-w-sm">
                <DialogHeader><DialogTitle>Depositar na carteira</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Label className="text-xs">Valor (R$ 5,00 a R$ 10.000,00)</Label>
                  <Input data-testid="deposit-amount" type="number" min="5" max="10000" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} />
                  <div className="grid grid-cols-4 gap-2">
                    {[20, 50, 100, 500].map(v => (
                      <Button key={v} variant="outline" size="sm" className="rounded-full" onClick={() => setDepositAmount(v)}>R$ {v}</Button>
                    ))}
                  </div>
                  <div className="space-y-2 pt-1">
                    {(() => {
                      const showPagarme = !!pagarmeStatus?.configured && methods.pagarme_pix !== false;
                      const showManual = methods.manual_pix !== false && methods.manual_pix_configured !== false;
                      const showCard = !!pagarmeStatus?.configured && methods.card !== false;
                      const showUsd = methods.stripe_configured !== false && methods.fx_usd !== false;
                      const showEur = methods.stripe_configured !== false && methods.fx_eur !== false;
                      const showCrypto = methods.crypto !== false;
                      return (
                        <>
                          {showPagarme && (
                            <Button data-testid="deposit-pagarme-pix" onClick={() => { setDepositDialogOpen(false); setPagarmePixOpen(true); }} className="w-full rounded-full h-11 gap-2 bg-emerald-600 hover:bg-emerald-700 text-white">
                              <QrCode className="w-4 h-4" /> Pix automático (QR Code Pagar.me) — instantâneo
                            </Button>
                          )}
                          {showManual && (
                            <Button data-testid="deposit-pix" variant={showPagarme ? "outline" : "default"} onClick={() => { setDepositDialogOpen(false); setPixOpen(true); }} className="w-full rounded-full h-11 gap-2">
                              <QrCode className="w-4 h-4" /> Pix manual para a chave do admin — envie o comprovante
                            </Button>
                          )}
                          {showCard && (
                            <Button data-testid="deposit-pagarme-card" variant="outline" onClick={() => { setDepositDialogOpen(false); setCardOpen(true); }} className="w-full rounded-full h-11 gap-2 border-primary/50 text-primary hover:bg-primary/10">
                              <CreditCard className="w-4 h-4" /> Cartão de crédito (até 12x)
                            </Button>
                          )}
                          {(showUsd || showEur) && (
                            <div className="grid grid-cols-2 gap-2">
                              {showUsd && <Button data-testid="deposit-fx-usd" variant="outline" onClick={() => { setDepositDialogOpen(false); setFxOpen("USD"); }} className="rounded-full h-11 gap-1.5"><DollarSign className="w-4 h-4 text-emerald-600" /> Dólar (US$)</Button>}
                              {showEur && <Button data-testid="deposit-fx-eur" variant="outline" onClick={() => { setDepositDialogOpen(false); setFxOpen("EUR"); }} className="rounded-full h-11 gap-1.5"><Euro className="w-4 h-4 text-sky-600" /> Euro (€)</Button>}
                            </div>
                          )}
                          {showCrypto && (
                            <Button data-testid="deposit-crypto" variant="outline" disabled={!methods.crypto_configured} title={!methods.crypto_configured ? "Aguardando configuração da chave CoinGate pelo admin" : ""} onClick={() => { setDepositDialogOpen(false); setCryptoTx(null); setCryptoOpen(true); }} className="w-full rounded-full h-11 gap-2 border-amber-500/50 text-amber-700 hover:bg-amber-500/10 disabled:opacity-60">
                              <Bitcoin className="w-4 h-4" /> Criptomoedas (USDT, BTC, ETH...){!methods.crypto_configured ? " — em breve" : ""}
                            </Button>
                          )}
                          {!showPagarme && !showManual && !showCard && !showUsd && !showEur && !showCrypto && (
                            <div className="text-sm text-amber-700 bg-amber-500/10 rounded-lg p-3 text-center" data-testid="deposit-no-methods">Depósitos temporariamente indisponíveis. Tente novamente em alguns minutos.</div>
                          )}
                          <div className="text-[11px] text-muted-foreground text-center">
                            {showPagarme ? "No Pix automático o saldo cai na hora. " : ""}{showManual ? "No Pix manual o admin confirma o comprovante e o saldo é creditado." : ""}
                          </div>
                        </>
                      );
                    })()}
                    {user?.is_admin && (
                      <>
                        <div className="flex items-center gap-2 pt-1">
                          <div className="flex-1 h-px bg-border" />
                          <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Modo Admin</span>
                          <div className="flex-1 h-px bg-border" />
                        </div>
                        <Button
                          data-testid="deposit-demo"
                          variant="outline"
                          onClick={async () => {
                            const amount = parseFloat(depositAmount);
                            if (!amount || amount < 1 || amount > 5000) {
                              toast.error("Valor demo entre R$ 1 e R$ 5.000");
                              return;
                            }
                            try {
                              const r = await axios.post(`${API}/wallet/deposit/demo`, { amount });
                              toast.success(`🧪 +R$ ${amount.toFixed(2)} creditados (demo). Saldo: R$ ${r.data.new_balance.toFixed(2)}`);
                              setDepositDialogOpen(false);
                              load();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Falha no depósito demo");
                            }
                          }}
                          className="w-full rounded-full h-11 gap-2 border-orange-500/40 text-orange-700 hover:bg-orange-500/10 dark:text-orange-300"
                        >
                          <FlaskConical className="w-4 h-4" /> Depositar (demo — bypass Pagar.me)
                        </Button>
                        <div className="text-[11px] text-muted-foreground text-center">
                          Credita saldo fictício direto. Só admin. Use para testar auto-topup + anúncios.
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <PixPaymentDialog
              open={pixOpen}
              onOpenChange={setPixOpen}
              purpose="wallet_deposit"
              amount={parseFloat(depositAmount) || 0}
              onMarkedPaid={() => setTimeout(load, 1500)}
            />
            <PagarmePixDialog
              open={pagarmePixOpen}
              onOpenChange={setPagarmePixOpen}
              flow="deposit"
              amount={parseFloat(depositAmount) || 0}
              onPaid={() => setTimeout(load, 800)}
            />
            <FxDepositDialog open={!!fxOpen} currency={fxOpen || "USD"} onOpenChange={(v) => !v && setFxOpen(null)} />
            <CryptoDepositDialog open={cryptoOpen} onOpenChange={setCryptoOpen} initialTx={cryptoTx} onCredited={load} />
            <CardPaymentDialog
              open={cardOpen}
              onOpenChange={setCardOpen}
              purpose="wallet_deposit"
              amount={parseFloat(depositAmount) || 0}
              title="Depositar com cartão"
              description="O saldo é creditado integralmente na carteira assim que o pagamento é aprovado."
              onSuccess={() => { setTimeout(load, 600); setCardsKey(k => k + 1); }}
            />

            <Dialog open={openW} onOpenChange={(v) => { setOpenW(v); if (!v) resetWithdraw(); }}>
              <DialogTrigger asChild>
                <Button data-testid="withdraw-btn" size="sm" variant="outline" className="rounded-full gap-1"><ArrowUpCircle className="w-4 h-4" />Sacar</Button>
              </DialogTrigger>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle>{wdStep === "form" ? "Solicitar saque" : "Confirme por e-mail"}</DialogTitle>
                </DialogHeader>
                {wdStep === "form" && (
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg bg-muted/50 text-xs">
                      Min R$ {data.min.toFixed(2)} · Max R$ {data.max.toFixed(2)} · Taxa {fee}%
                      <div className="mt-1">
                        Modo: <strong>{data.withdrawal_mode === "auto" ? "Automático (instantâneo)" : "Aprovação manual"}</strong>
                      </div>
                    </div>
                    <Input data-testid="withdraw-amount" type="number" placeholder="Valor R$" value={withdrawForm.amount} onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })} />
                    <Select value={withdrawForm.method} onValueChange={(v) => setWithdrawForm({ ...withdrawForm, method: v })}>
                      <SelectTrigger data-testid="withdraw-method"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pix">PIX</SelectItem>
                        <SelectItem value="bank">Transferência bancária</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input data-testid="withdraw-destination" placeholder={withdrawForm.method === "pix" ? "Sua chave PIX" : "Banco, agência e conta"} value={withdrawForm.destination} onChange={(e) => setWithdrawForm({ ...withdrawForm, destination: e.target.value })} />
                    {withdrawForm.amount && (
                      <div className="text-xs text-muted-foreground">
                        Você receberá: <strong className="text-green-600">R$ {(parseFloat(withdrawForm.amount) * (1 - fee / 100)).toFixed(2)}</strong>
                      </div>
                    )}
                    <Button data-testid="withdraw-submit" onClick={requestWithdraw} disabled={wdSubmitting} className="w-full rounded-full">
                      {wdSubmitting ? "Enviando..." : "Enviar código por e-mail"}
                    </Button>
                  </div>
                )}
                {wdStep === "code" && (
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg bg-muted/50 text-xs">
                      Enviamos um código de 6 dígitos para <strong>{wdRequest?.email_sent_to}</strong>. Ele expira em 10 minutos.
                    </div>
                    {wdRequest?.mocked && wdRequest?.dev_code && (
                      <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/40 text-xs">
                        <strong>Modo dev (sem Resend API key):</strong> use o código <code className="font-mono text-base">{wdRequest.dev_code}</code>
                      </div>
                    )}
                    <Input
                      data-testid="withdraw-code"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      value={wdCode}
                      onChange={(e) => setWdCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="text-center text-2xl tracking-[0.5em] font-mono"
                    />
                    <div className="flex gap-2">
                      <Button data-testid="withdraw-back" variant="outline" onClick={() => setWdStep("form")} className="rounded-full">Voltar</Button>
                      <Button data-testid="withdraw-confirm" onClick={confirmWithdraw} disabled={wdSubmitting} className="flex-1 rounded-full">
                        {wdSubmitting ? "Confirmando..." : "Confirmar saque"}
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </Card>

        {/* Investor card */}
        <Card className="p-6 bg-gradient-to-br from-green-500/10 to-transparent border-green-500/30" data-testid="investor-card">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Investimentos</div>
            <TrendingUp className="w-4 h-4 text-green-600" />
          </div>
          <div className="text-4xl font-bold tracking-tight" data-testid="investor-total">R$ {investor.total_invested.toFixed(2)}</div>
          <div className="text-xs text-muted-foreground mt-1">
            +R$ {investor.accrued_interest.toFixed(2)} rendimento acumulado
          </div>
        </Card>
      </div>

      {/* Auto-deposit configuration */}
      <WithdrawGateCard refreshKey={data?.wallet_balance} />
      <AutoDepositCard />
      <SavedCardsCard refreshKey={cardsKey} />
      <ReceiptDialog />

      <Tabs defaultValue="wtx">
        <TabsList className="rounded-full">
          <TabsTrigger value="wtx" className="rounded-full">Histórico carteira</TabsTrigger>
          <TabsTrigger value="wdr" className="rounded-full">Meus saques</TabsTrigger>
          <TabsTrigger value="inv" className="rounded-full">Meus investimentos</TabsTrigger>
        </TabsList>

        <TabsContent value="wtx" className="mt-4 space-y-2">
          {data.transactions.length === 0 && <Card className="p-8 text-center text-muted-foreground">Sem movimentações</Card>}
          {data.transactions.map(t => (
            <Card key={t.tx_id} className="p-3 flex items-center justify-between" data-testid={`wtx-${t.tx_id}`}>
              <div className="flex items-center gap-3">
                {t.amount > 0 ? <ArrowDownCircle className="w-5 h-5 text-green-600" /> : <ArrowUpCircle className="w-5 h-5 text-orange-500" />}
                <div>
                  <div className="font-medium text-sm">{t.type === "deposit" ? "Depósito" : t.type === "withdrawal_hold" ? "Saque solicitado" : t.type}</div>
                  <div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString()}</div>
                </div>
              </div>
              <div className={`font-bold ${t.amount > 0 ? "text-green-600" : "text-orange-500"}`}>{t.amount > 0 ? "+" : ""}R$ {t.amount.toFixed(2)}</div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="wdr" className="mt-4 space-y-2">
          {data.withdrawals.length === 0 && <Card className="p-8 text-center text-muted-foreground">Sem saques</Card>}
          {data.withdrawals.map(w => (
            <Card key={w.withdrawal_id} className="p-4" data-testid={`wdr-${w.withdrawal_id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold">R$ {w.amount.toFixed(2)}</span>
                    <Badge className={statusStyle[w.status]}>{statusLabel[w.status]}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {w.method === "pix" ? "PIX" : "Bancário"} · {w.destination.substring(0, 30)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Taxa R$ {w.fee.toFixed(2)} · Receberá R$ {w.net_amount.toFixed(2)}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground">{new Date(w.created_at).toLocaleDateString()}</div>
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="inv" className="mt-4 space-y-2">
          {investor.investments.length === 0 && (
            <Card className="p-8 text-center text-muted-foreground">
              <TrendingUp className="w-10 h-10 mx-auto mb-3 opacity-30" />
              Nenhum investimento. <a href="/investors" className="text-primary hover:underline">Ver planos</a>
            </Card>
          )}
          {investor.investments.map(inv => {
            const unlockDt = new Date(inv.unlock_at);
            const now = new Date();
            const locked = unlockDt > now;
            const daysLeft = Math.max(0, Math.ceil((unlockDt - now) / (1000 * 60 * 60 * 24)));
            return (
              <Card key={inv.investment_id} className="p-4" data-testid={`inv-${inv.investment_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold">{inv.plan_name}</span>
                      <Badge className={inv.status === "active" ? "bg-green-600" : inv.status === "withdrawing" ? "bg-orange-500" : "bg-muted"}>
                        {inv.status === "active" ? "Ativo" : inv.status === "withdrawing" ? "Em resgate" : inv.status}
                      </Badge>
                    </div>
                    <div className="text-sm">
                      <span className="font-bold text-primary">R$ {inv.amount.toFixed(2)}</span> · {inv.roi_pct}% a.a. · {inv.term_months}m
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {locked ? (
                        <><Clock className="w-3 h-3 inline mr-1" /> Bloqueado por mais {daysLeft} dias (carência)</>
                      ) : (
                        <><CheckCircle2 className="w-3 h-3 inline mr-1 text-green-600" /> Liberado para saque</>
                      )}
                    </div>
                  </div>
                  {inv.status === "active" && !locked && (
                    <Button size="sm" className="rounded-full" data-testid={`inv-withdraw-${inv.investment_id}`} onClick={() => setOpenInvW(inv)}>
                      Sacar
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </TabsContent>
      </Tabs>

      {/* Investor withdraw dialog */}
      <Dialog open={!!openInvW} onOpenChange={(v) => !v && setOpenInvW(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Sacar {openInvW?.plan_name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {openInvW && (
              <div className="p-3 rounded-lg bg-muted/50 text-xs">
                Principal: R$ {openInvW.amount.toFixed(2)} · ROI {openInvW.roi_pct}%
              </div>
            )}
            <Select value={invWForm.method} onValueChange={(v) => setInvWForm({ ...invWForm, method: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pix">PIX</SelectItem>
                <SelectItem value="bank">Transferência bancária</SelectItem>
              </SelectContent>
            </Select>
            <Input data-testid="inv-withdraw-destination" placeholder={invWForm.method === "pix" ? "Chave PIX" : "Banco, agência e conta"} value={invWForm.destination} onChange={(e) => setInvWForm({ ...invWForm, destination: e.target.value })} />
            <Button data-testid="inv-withdraw-submit" onClick={withdrawInvestment} className="w-full rounded-full">Confirmar saque</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
